import {PaperAirplaneIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import type {IconButtonProps} from '@primer/react'
import {clsx} from 'clsx'
import styles from './ChatInputDisplay.module.css'

export interface ChatInputDisplayProps {
  /** Current value of the input */
  value: string
  /** Called when the value changes */
  onChange: (value: string) => void
  /** Called when the user submits (Enter or button click) */
  onSubmit: () => void
  /** Placeholder text */
  placeholder?: string
  /** Whether the input is disabled */
  disabled?: boolean
  /** Whether a submission is in progress */
  isSubmitting?: boolean
  /** Aria label for the textarea */
  ariaLabel?: string
  /** Aria label for the send button */
  sendButtonAriaLabel?: string
  /** Called when user pastes into textarea (for image upload handling) */
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  /** Optional content to render before the send button (e.g., ImageAttachButton) */
  toolbarSlot?: React.ReactNode
  /** Optional content to render on the right side of the toolbar, next to the send button (e.g., ModelPicker) */
  toolbarEndSlot?: React.ReactNode
  /** Optional stop button rendered next to the send button when a session is active */
  stopSlot?: React.ReactNode
  /** Ref to the textarea element */
  ref?: React.Ref<HTMLTextAreaElement>
  /** Whether to allow submission with empty input (default: false - button is inactive when empty) */
  allowEmptySubmit?: boolean
  /** Direction for button tooltips (e.g. 'n' when rendered at the bottom of a side panel) */
  tooltipDirection?: IconButtonProps['tooltipDirection']
}

export function ChatInputDisplay({
  value,
  onChange,
  onSubmit,
  placeholder = 'Type a message...',
  disabled = false,
  isSubmitting = false,
  ariaLabel,
  sendButtonAriaLabel = 'Send',
  onPaste,
  toolbarSlot,
  toolbarEndSlot,
  stopSlot,
  ref,
  allowEmptySubmit = false,
  tooltipDirection,
}: ChatInputDisplayProps) {
  const isEmpty = !value.trim()
  const isButtonInactive = disabled || isSubmitting || (!allowEmptySubmit && isEmpty)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      if (!disabled && !isSubmitting && (allowEmptySubmit || !isEmpty)) {
        onSubmit()
      }
    }
  }

  const handleSubmitClick = () => {
    if (!disabled && !isSubmitting && (allowEmptySubmit || !isEmpty)) {
      onSubmit()
    }
  }

  const hasToolbar = !!toolbarSlot || !!toolbarEndSlot

  return (
    <div className={styles.container}>
      <div className={clsx(styles.inputWrapper, hasToolbar && styles.inputWrapperWithToolbar)}>
        <textarea
          ref={ref}
          className={clsx(styles.input, hasToolbar && styles.inputWithToolbar)}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={onPaste}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel ?? placeholder}
        />
        {hasToolbar ? (
          <div className={styles.toolbar}>
            <div className={styles.toolbarGroup}>{toolbarSlot}</div>
            <div className={styles.toolbarGroup}>
              {toolbarEndSlot}
              <div className={clsx(toolbarEndSlot && styles.submitWithDivider, stopSlot && styles.submitActions)}>
                {stopSlot}
                <IconButton
                  variant="invisible"
                  size="medium"
                  icon={PaperAirplaneIcon}
                  aria-label={sendButtonAriaLabel}
                  tooltipDirection={tooltipDirection}
                  inactive={isButtonInactive}
                  aria-disabled={isButtonInactive}
                  onClick={handleSubmitClick}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className={stopSlot ? styles.sendButtonGroup : undefined}>
            {stopSlot}
            <IconButton
              variant="invisible"
              size="medium"
              icon={PaperAirplaneIcon}
              aria-label={sendButtonAriaLabel}
              tooltipDirection={tooltipDirection}
              inactive={isButtonInactive}
              aria-disabled={isButtonInactive}
              onClick={handleSubmitClick}
              className={stopSlot ? undefined : styles.sendButton}
            />
          </div>
        )}
      </div>
    </div>
  )
}
